/**
 * Product taxonomy — 5-capable category tree (S0.4).
 *
 * Single primary-FK model: a Product carries ONE `categoryId` pointing at a
 * node in this tree (leaf-preferred, soft-warn non-leaf). The codex-era
 * 3-FK columns (classId / productGroupId / productSubGroupId) are deprecated
 * and die in S1.2.
 *
 * Capabilities:
 * - depth 1..5 capable; 3 levels seeded/enabled by default, L4/L5 hidden by
 *   `CategoryLevelConfig.enabled` (hide-don't-disable).
 * - renamable bilingual level labels (the「層級名稱 Level Names」facility).
 * - Hidden-level read rule: nodes at a disabled level remain in `categoryPath`
 *   breadcrumbs and product read views; they are excluded ONLY from
 *   TaxonomyCascader new-selection (`forSelection` reads).
 *
 * Board amendment D-TAXONOMY-DEPTH (2026-07-17, recorded in
 * ERP/figjam/product-master-house/APPROVED-FORMAT.md) supersedes the
 * 2026-07-14 MAX_DEPTH-3 lock.
 */

export type CategoryDepth = 1 | 2 | 3 | 4 | 5;

export const MAX_CATEGORY_DEPTH = 5;

/** Levels 1..3 are the seeded default and can never be disabled. */
export const MIN_ENABLED_CATEGORY_DEPTH = 3;

/**
 * External catalog cross-references for a category node (Slice 5.1).
 * Capture-only — no live sync to GS1 / Google is performed (see plan Appendix C).
 * FE api-spec aspiration until backend confirms persistence (OQ-4).
 */
export interface CategoryExternalMappings {
  /** GS1 GPC brick / segment code. */
  gs1?: string;
  /** Google Product Taxonomy id or path. */
  google?: string;
}

export interface ProductCategory {
  id: string;
  /** Immutable business code; unique across ALL nodes (active + inactive), never reused. */
  code: string;
  parentId: string | null;
  /** Always `parent.depth + 1` (1 for roots); hard cap 5. */
  depth: CategoryDepth;
  /** Materialized path of node ids from root to self, '/'-joined (derived, recomputed on reparent). */
  path: string;
  sortOrder: number;
  nameEn: string;
  nameZh: string;
  isActive: boolean;
  /** Read-only NetSuite custom-segment mapping (Tier-1 reference); optional. */
  netsuiteSegmentValueId?: string;
  // --- Enrich fields (Slice 5.1) — FE api-spec aspiration until backend confirms (OQ-4).
  //     Wired to input UI only in the gated Slice 5.2b; captured/round-tripped in MSW now. ---
  /** Optional longer description of the category. */
  description?: string;
  /** Optional icon key (design-system icon name) for the node. */
  iconKey?: string;
  /** Optional search-synonyms that widen matching in the picker. */
  synonyms?: string[];
  /** Optional external catalog cross-references (GS1 / Google); capture-only. */
  externalMappings?: CategoryExternalMappings;
}

export interface CategoryLevelConfig {
  level: CategoryDepth;
  labelEn: string;
  labelZh: string;
  /** L1..L3 always true (cannot be disabled); L4/L5 default false. */
  enabled: boolean;
}

/**
 * S0.4 rule: no level label may duplicate a nav group name (the L1 default was
 * renamed off 系統/System for exactly this collision). Local copy of the Board-E
 * nav group names until S0.2's `ofcVocabulary.ts` becomes the single source.
 */
export const NAV_GROUP_LABEL_DENYLIST: readonly string[] = [
  'Master Data',
  '主資料',
  'Sales',
  '銷售',
  'Procurement',
  '採購',
  'Order Control Center',
  '訂單控管中心',
  'Shipping',
  '船務',
  'Finance',
  '會計',
  'Reports',
  '報表',
  'System',
  '系統',
];

// ---------------------------------------------------------------------------
// Request / response contracts (/product-categories, /category-levels)
// ---------------------------------------------------------------------------

export interface ProductCategoryListParams {
  /** When true, only active nodes at enabled levels (TaxonomyCascader new-selection). */
  forSelection?: boolean;
  includeInactive?: boolean;
}

export interface ProductCategoryListResponse {
  data: ProductCategory[];
}

export interface ProductCategoryDetailResponse {
  data: ProductCategory;
}

export interface CreateProductCategoryRequest {
  code: string;
  nameEn: string;
  nameZh: string;
  parentId: string | null;
  sortOrder?: number;
  netsuiteSegmentValueId?: string;
  comment?: string;
}

/**
 * `code` is immutable — rename touches names/sort/NetSuite ref only.
 * The enrich fields (`description`/`iconKey`/`synonyms`/`externalMappings`) are a
 * Slice 5.1 FE api-spec aspiration (OQ-4): accepted + persisted + audited by the
 * MSW handler now, but only exposed via input UI once backend persistence is
 * confirmed (the gated Slice 5.2b).
 */
export interface UpdateProductCategoryRequest {
  nameEn?: string;
  nameZh?: string;
  sortOrder?: number;
  netsuiteSegmentValueId?: string;
  description?: string;
  iconKey?: string;
  synonyms?: string[];
  externalMappings?: CategoryExternalMappings;
  /** Mandatory change comment (audited). */
  comment: string;
}

export interface ReparentProductCategoryRequest {
  newParentId: string | null;
  /** Mandatory change comment (audited). */
  comment: string;
}

export interface DeactivateProductCategoryRequest {
  /** Required whenever products are assigned to the node or its subtree. */
  replacementId?: string;
  /** Mandatory change comment (audited). */
  comment: string;
}

export interface DeactivateProductCategoryResponse {
  data: ProductCategory;
  /** Products moved onto `replacementId` (whole subtree counted). */
  reassignedProducts: number;
}

export interface CategoryLevelListResponse {
  data: CategoryLevelConfig[];
}

export interface UpdateCategoryLevelRequest {
  labelEn?: string;
  labelZh?: string;
  enabled?: boolean;
  /** Mandatory change comment (audited). */
  comment: string;
}

// ---------------------------------------------------------------------------
// Audit rows — every taxonomy mutation writes one (feeds the W10 audit lens)
// ---------------------------------------------------------------------------

export type CategoryAuditAction = 'create' | 'rename' | 'reparent' | 'deactivate' | 'level_update';

export interface CategoryAuditRow {
  id: string;
  at: string;
  action: CategoryAuditAction;
  /** Category id for node actions; the level number (as string) for `level_update`. */
  targetId: string;
  comment: string;
  // CU-1b: `before`/`after` value snapshots are RECORDED on both sides (the
  // MSW store; `product_category_audit`'s jsonb columns) but are NOT on this
  // READ contract — the frozen SDL's `ProductTaxonomyAuditEntry` carries the
  // action/target/comment triple only. Serving them would mean adding an
  // untyped `JSON` scalar to a just-frozen schema for a field with zero
  // consumers; the W10 audit lens grows a TYPED per-action payload instead.
  // Keeping dead optional mirrors here is the D-PROD-G15 anti-pattern, so
  // they are gone from the type rather than silently unpopulated.
}

export interface CategoryAuditListResponse {
  data: CategoryAuditRow[];
}

/** Machine-readable guard failures shared by the MSW handler and vitest. */
export type CategoryGuardError =
  | 'PARENT_NOT_FOUND'
  | 'PARENT_INACTIVE'
  | 'DEPTH_EXCEEDED'
  | 'LEVEL_DISABLED'
  | 'CODE_TAKEN'
  | 'NODE_NOT_FOUND'
  | 'CYCLE'
  | 'SUBTREE_DEPTH_EXCEEDED'
  | 'REPLACEMENT_REQUIRED'
  | 'REPLACEMENT_INVALID'
  | 'COMMENT_REQUIRED'
  | 'NAV_LABEL_COLLISION'
  | 'CORE_LEVEL_LOCKED';
